import { computed, nextTick, toValue, type MaybeRef, type Ref } from 'vue';

export type UseKeyboardGroupNavigationOptions<T> = {
  /** Ref to the container that has [role="tab"] or [role="radio"] children */
  containerRef: Ref<HTMLElement | null>;
  /** Role of the focusable items (used to query selector). Use "tab" for tablist, "radio" for radiogroup. */
  role: 'tab' | 'radio';
  /** List of items. Can be a ref or computed. */
  items: MaybeRef<readonly T[]>;
  /** Returns the string value that identifies an item (e.g. item.name). */
  getValue: (item: T) => string;
  /** Current selected value. Can be a ref or computed. */
  selectedValue: MaybeRef<string>;
  /**
   * Called when the user selects an item via keyboard (Arrow keys, Home, End).
   * Focus is applied by the composable after nextTick.
   */
  onSelect: (value: string, item: T, index: number) => void;
};

/**
 * Handles keyboard navigation (Arrow keys, Home, End) within a group of focusable items
 * (tabs, radiogroup). Use on the container; children must have the given role and roving tabindex.
 */
export function useKeyboardGroupNavigation<T>(
  options: UseKeyboardGroupNavigationOptions<T>,
) {
  const { containerRef, role, items, getValue, selectedValue, onSelect } =
    options;

  const selectedIndex = computed(() => {
    const list = toValue(items);
    const current = toValue(selectedValue);
    return list.findIndex((item) => getValue(item) === current);
  });

  const focusOption = (index: number) => {
    const container = toValue(containerRef);
    const el = container?.querySelectorAll<HTMLElement>(`[role="${role}"]`)?.[
      index
    ];
    el?.focus();
  };

  const onKeydown = (event: KeyboardEvent) => {
    const list = toValue(items);
    const len = list.length;
    if (len === 0) return;
    const currentRaw = selectedIndex.value;
    const current = currentRaw < 0 ? 0 : currentRaw;
    let next = current;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      next = (current + 1) % len;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      next = (current - 1 + len) % len;
    } else if (event.key === 'Home') {
      event.preventDefault();
      next = 0;
    } else if (event.key === 'End') {
      event.preventDefault();
      next = len - 1;
    } else return;
    const item = list[next];
    const value = getValue(item);
    onSelect(value, item, next);
    nextTick(() => focusOption(next));
  };

  return { onKeydown };
}
